import { useHistory } from 'react-router-dom';
import { useDispatch, batch } from 'react-redux';
import * as Sentry from '@sentry/react';
import * as amplitude from '@amplitude/analytics-browser';

import { REMOVE_USER, setUserReady } from '../actions/userActions';
import { removeItem } from '../helpers/store';
import { trackEvent, reset as analyticsReset } from '../helpers/analytics';
import { restartChat } from '../helpers/supportChat';
import { useSignOutMutation } from '../services/auth';
import { useResetCache } from './useResetCache';
import { setNetworksReady } from '../actions/networksActions';
import { signOutBrowserExtension } from '../services/chromeExtension';
import { MFA_SETUP_FROM_KEY, MFA_SETUP_TOKEN_KEY, clearMfaChallengeStorage } from './useSignIn';

export const useSignOut = () => {
  const [signOutMutation, { isLoading }] = useSignOutMutation();
  const resetCache = useResetCache();
  const history = useHistory();
  const dispatch = useDispatch();

  const signOut = () => {
    trackEvent('user_logged_out');

    const findemAuthBaseUrl = import.meta.env.VITE_FINDEM_AUTH_BASE_URL;
    if (findemAuthBaseUrl) {
      fetch(`${findemAuthBaseUrl}/auth/logout?api=1`, {
        credentials: 'include',
        keepalive: true,
      }).catch(() => {});
    }

    signOutMutation().then(() => {
      signOutBrowserExtension();
      removeItem('authToken');
      // Drop half-completed MFA login/setup tokens (challenge token + persisted
      // destination/invite flag, plus setup token/from) so they can't linger on a shared
      // device or mis-route a later login.
      clearMfaChallengeStorage();
      removeItem(MFA_SETUP_TOKEN_KEY);
      removeItem(MFA_SETUP_FROM_KEY);
      batch(() => {
        dispatch({ type: REMOVE_USER });
        dispatch(setUserReady(true));
        dispatch(setNetworksReady(true));
      });
      analyticsReset();
      Sentry.configureScope((scope) => scope.setUser(null));
      amplitude.reset();
      resetCache();
      restartChat();
      window.engagement?.shutdown();
      history.push('/login');
    });
  };

  return { signOut, isLoading };
};
